import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaTrophy } from 'react-icons/fa';
import { BetDetails, Future } from '../../../../models/MyBetModel.tsx';
import { FutureGames } from '../future/FutureGames.tsx';
import { useSport } from '../../../../context/SportContext.tsx';
import { futureGames } from '../../../../services/player/apis/Schelude.tsx';
import {
    formatGolfTournamentName,
    getGolfFutureSubjectLabel,
    getPgaFutureTabs,
    GolfFutureTab,
    isFutureGameLive,
    PGA_LEAGUE_NAME,
    stripGolfTournamentFromMarket,
} from '../../../../helpers/golfFuturesLive.ts';
import { useVisibilityAdaptiveInterval } from '../../../../hooks/useVisibilityAdaptiveInterval.ts';
import { filterPreferredSportsbookOdds } from '../../../../helpers/functions.tsx';
import '../../../../styles/Future.css';

interface ICommercialGolfFutures {
    onShowBetsModal: (incomingBets?: BetDetails[]) => void;
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
}

const SKELETON_ROWS = 6;

function GolfFuturesListSkeleton() {
    return (
        <>
            {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <div key={i} className="odd-item golf-futures-row-skeleton">
                    <span className="golf-futures-name-skeleton golf-futures-sk" />
                    <span className="golf-futures-odd-skeleton golf-futures-sk" />
                </div>
            ))}
        </>
    );
}

function GolfFuturesPageSkeleton() {
    return (
        <div className="golf-futures-live" aria-busy="true">
            <div className="golf-futures-league">
                <span className="golf-futures-sk golf-futures-sk-title" />
            </div>
            <div className="golf-futures-event">
                <div className="golf-futures-event-main">
                    <span className="golf-futures-sk golf-futures-sk-trophy" />
                    <span className="golf-futures-sk golf-futures-sk-event" />
                </div>
            </div>
            <div className="golf-futures-tabs">
                <span className="golf-futures-sk golf-futures-sk-tab golf-futures-sk-tab--lg" />
                <span className="golf-futures-sk golf-futures-sk-tab" />
                <span className="golf-futures-sk golf-futures-sk-tab golf-futures-sk-tab--md" />
                <span className="golf-futures-sk golf-futures-sk-tab" />
                <span className="golf-futures-sk golf-futures-sk-tab golf-futures-sk-tab--md" />
            </div>
            <div className="golf-futures-list">
                <div className="golf-futures-columns">
                    <span className="golf-futures-sk golf-futures-sk-col" />
                    <span className="golf-futures-sk golf-futures-sk-col golf-futures-sk-col--right" />
                </div>
                <GolfFuturesListSkeleton />
            </div>
        </div>
    );
}

function preferPrimaryBookOdds(game: Future, leagueKey: string): Future {
    return {
        ...game,
        odds: filterPreferredSportsbookOdds(game.odds ?? []),
        leagueKey,
    };
}

function CommercialGolfFutures({
    onShowBetsModal,
    setErrorMessage,
}: ICommercialGolfFutures) {
    const { sports } = useSport();
    const tabs = useMemo(() => getPgaFutureTabs(sports), [sports]);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [gamesByKey, setGamesByKey] = useState<Record<string, Future[]>>({});
    const [loadingKey, setLoadingKey] = useState<string | null>(null);
    const selectedKeyRef = useRef(selectedKey);
    selectedKeyRef.current = selectedKey;
    const tabsRef = useRef(tabs);
    tabsRef.current = tabs;
    const tabsRowRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef({ x: 0, moved: false, mouseDown: false, startScroll: 0 });

    const selectedTab = tabs.find(tab => tab.leagueKey === selectedKey) ?? null;
    const selectedGames = selectedKey ? (gamesByKey[selectedKey] ?? []) : [];
    const hasLoadedSelected = Boolean(
        selectedKey && Object.prototype.hasOwnProperty.call(gamesByKey, selectedKey)
    );
    const hasAnyLoadedMarket = Object.keys(gamesByKey).length > 0;
    const showFullSkeleton = !hasAnyLoadedMarket;
    const showOddsSkeleton = hasAnyLoadedMarket && (!hasLoadedSelected || loadingKey === selectedKey);
    const headerGame = selectedGames[0];
    const loadedTournamentName =
        formatGolfTournamentName(headerGame) ||
        formatGolfTournamentName(Object.values(gamesByKey).flat()[0]);
    const tournamentName = loadedTournamentName || 'PGA';
    const showLiveBadge = [
        ...selectedGames,
        ...Object.values(gamesByKey).flat(),
    ].some(game => isFutureGameLive(game));
    const tabLabel = (tabName?: string | null) =>
        stripGolfTournamentFromMarket(tabName, tournamentName) || tabName || '';
    const subjectLabel = getGolfFutureSubjectLabel(headerGame, selectedTab?.name);
    const marketLabel = (tabLabel(selectedTab?.name) || 'To Win').toUpperCase();

    const loadTabGames = useCallback(async (tab: GolfFutureTab, silent = false) => {
        if (!silent) setLoadingKey(tab.leagueKey);
        try {
            const gamesData =
                (await futureGames({ league: PGA_LEAGUE_NAME, future: tab.selections })) ?? [];
            const mapped = gamesData
                .filter((g: any) => !g.isEmpty)
                .map(game => preferPrimaryBookOdds(game, tab.leagueKey));

            setGamesByKey(prev => ({
                ...prev,
                [tab.leagueKey]: mapped,
            }));
        } catch (err) {
            console.error('Error loading golf future market:', err);
            if (!silent) {
                setErrorMessage('Error loading golf future games.');
            }
        } finally {
            if (!silent) {
                setLoadingKey(prev => (prev === tab.leagueKey ? null : prev));
            }
        }
    }, [setErrorMessage]);

    useEffect(() => {
        if (tabs.length === 0) {
            setSelectedKey(null);
            return;
        }

        if (selectedKey && !tabs.some(tab => tab.leagueKey === selectedKey)) {
            setSelectedKey(null);
            return;
        }

        setSelectedKey(prev => {
            if (prev && tabs.some(tab => tab.leagueKey === prev)) return prev;
            return tabs[0].leagueKey;
        });
    }, [tabs, selectedKey]);

    useEffect(() => {
        if (!selectedKey) return;
        const tab = tabs.find(t => t.leagueKey === selectedKey);
        if (!tab || gamesByKey[selectedKey]) return;
        void loadTabGames(tab);
    }, [selectedKey, tabs, gamesByKey, loadTabGames]);

    const endMouseDrag = () => {
        dragRef.current.mouseDown = false;
        tabsRowRef.current?.classList.remove('is-dragging');
    };

    const hasTabsRow = tabs.length > 0 && !showFullSkeleton;

    useEffect(() => {
        const el = tabsRowRef.current;
        if (!el) return;

        const onWheel = (event: WheelEvent) => {
            if (el.scrollWidth <= el.clientWidth) return;
            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            if (delta === 0) return;
            const maxScroll = el.scrollWidth - el.clientWidth;
            const atStart = el.scrollLeft <= 0 && delta < 0;
            const atEnd = el.scrollLeft >= maxScroll - 1 && delta > 0;
            if (atStart || atEnd) return;
            event.preventDefault();
            el.scrollLeft += delta;
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [hasTabsRow]);

    const handleTabClick = (tab: GolfFutureTab) => {
        if (dragRef.current.moved) return;
        setSelectedKey(tab.leagueKey);
        if (!gamesByKey[tab.leagueKey]) {
            void loadTabGames(tab);
        }
    };

    useVisibilityAdaptiveInterval(
        async () => {
            const key = selectedKeyRef.current;
            const tab = tabsRef.current.find(t => t.leagueKey === key);
            if (!tab) return;
            await loadTabGames(tab, true);
        },
        Boolean(selectedTab),
        { activeMs: 7000, pauseWhenHidden: true }
    );

    if (tabs.length === 0) {
        return (
            <div className="golf-futures-live">
                <div className="golf-futures-league">
                    <span className="golf-futures-title">PGA Live</span>
                </div>
                <div className="golf-futures-empty">
                    There are currently no odds available for this market.
                </div>
            </div>
        );
    }

    if (showFullSkeleton) {
        return <GolfFuturesPageSkeleton />;
    }

    return (
        <div className="golf-futures-live">
            <div className="golf-futures-league">
                <span className="golf-futures-title">PGA Live</span>
            </div>

            <div className="golf-futures-event">
                <div className="golf-futures-event-main">
                    <FaTrophy className="golf-futures-trophy" aria-hidden="true" />
                    <span className="golf-futures-event-name">{tournamentName}</span>
                </div>
                {showLiveBadge && <span className="golf-futures-live-badge">LIVE</span>}
            </div>

            <div
                className="golf-futures-tabs"
                ref={tabsRowRef}
                role="tablist"
                aria-label="Golf future markets"
                onPointerDown={(event) => {
                    dragRef.current = {
                        x: event.clientX,
                        moved: false,
                        mouseDown: event.pointerType === 'mouse' && event.button === 0,
                        startScroll: tabsRowRef.current?.scrollLeft ?? 0,
                    };
                }}
                onPointerMove={(event) => {
                    const dx = event.clientX - dragRef.current.x;
                    if (Math.abs(dx) > 8) {
                        dragRef.current.moved = true;
                    }
                    if (!dragRef.current.mouseDown || !dragRef.current.moved) return;
                    if ((event.buttons & 1) === 0) {
                        dragRef.current.mouseDown = false;
                        return;
                    }
                    const el = tabsRowRef.current;
                    if (!el) return;
                    el.classList.add('is-dragging');
                    el.scrollLeft = dragRef.current.startScroll - dx;
                }}
                onPointerUp={endMouseDrag}
                onPointerLeave={endMouseDrag}
                onPointerCancel={endMouseDrag}
            >
                {tabs.map(tab => (
                    <button
                        key={tab.leagueKey}
                        type="button"
                        role="tab"
                        data-tab-key={tab.leagueKey}
                        aria-selected={selectedKey === tab.leagueKey}
                        className={`golf-futures-tab ${selectedKey === tab.leagueKey ? 'active' : ''}`}
                        onClick={() => handleTabClick(tab)}
                    >
                        {tabLabel(tab.name)}
                    </button>
                ))}
            </div>

            <div className="golf-futures-list" role="tabpanel">
                    <div className="golf-futures-columns">
                        <span style={{ fontSize: '14px' }}>{subjectLabel}</span>
                        <span style={{ fontSize: '14px' }}>{marketLabel}</span>
                    </div>
                    {showOddsSkeleton ? (
                        <GolfFuturesListSkeleton />
                    ) : selectedGames.length === 0 ? (
                        <div className="golf-futures-empty">
                            There are currently no odds available for this market.
                        </div>
                    ) : (
                        selectedGames.map(game => (
                            <FutureGames
                                key={game.id ?? game.leagueKey}
                                game={game}
                                onShowBetsModal={onShowBetsModal}
                                setErrorMessage={setErrorMessage}
                                compact
                            />
                        ))
                    )}
            </div>
        </div>
    );
}

export default CommercialGolfFutures;
